/**
 * Top-bar settings menu — theme · density · number format.
 *
 * Lives next to the user avatar in <AppLayout>. Click the gear to open
 * a popover with grouped toggle pills. Choices persist to localStorage
 * via `usePrefs()`.
 */

import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Settings2, Sun, Moon, Monitor, Rows3, Rows4, DollarSign, Hash,
  ArrowUpRight,
} from 'lucide-react'
import { usePrefs } from '@/data/preferences'

export default function PreferencesMenu({ compact = false }) {
  const { prefs, update } = usePrefs()
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className={[
          'inline-flex items-center justify-center rounded-full border transition',
          'border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.05]',
          'data-[theme=light]:border-black/10 data-[theme=light]:bg-black/[0.03]',
          compact ? 'w-9 h-9' : 'w-9 h-9',
        ].join(' ')}
        title="Display preferences"
        aria-label="Display preferences"
      >
        <Settings2 className="w-4 h-4 text-fg-muted" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
            className="absolute right-0 mt-2 w-72 rounded-2xl border border-white/[0.08] bg-navy-800/95 backdrop-blur-xl shadow-2xl p-4 pp-menu z-40"
          >
            <Group label="Theme">
              <Pill
                active={prefs.theme === 'light'}
                onClick={() => update({ theme: 'light' })}
                icon={Sun}
                label="Light"
              />
              <Pill
                active={prefs.theme === 'dark'}
                onClick={() => update({ theme: 'dark' })}
                icon={Moon}
                label="Dark"
              />
              <Pill
                active={prefs.theme === 'system'}
                onClick={() => update({ theme: 'system' })}
                icon={Monitor}
                label="System"
              />
            </Group>

            <Group label="Density">
              <Pill
                active={prefs.density === 'comfortable'}
                onClick={() => update({ density: 'comfortable' })}
                icon={Rows3}
                label="Comfortable"
              />
              <Pill
                active={prefs.density === 'compact'}
                onClick={() => update({ density: 'compact' })}
                icon={Rows4}
                label="Compact"
              />
            </Group>

            <Group label="Numbers">
              <Pill
                active={prefs.numberFormat === 'full'}
                onClick={() => update({ numberFormat: 'full' })}
                icon={DollarSign}
                label="$604,347"
              />
              <Pill
                active={prefs.numberFormat === 'abbreviated'}
                onClick={() => update({ numberFormat: 'abbreviated' })}
                icon={Hash}
                label="$604K"
              />
            </Group>

            <Link
              to="/dashboard/settings"
              onClick={() => setOpen(false)}
              className="mt-3 flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.05] text-[11px] text-white/80 transition"
            >
              <span>More personalization options…</span>
              <ArrowUpRight className="w-3 h-3 text-white/45" />
            </Link>
            <div className="mt-2 pt-2 border-t border-white/[0.05] font-mono text-[9.5px] text-white/35 leading-relaxed">
              Saved per browser · changes apply immediately
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

function Group({ label, children }) {
  return (
    <div className="mb-3 last:mb-0">
      <div className="font-mono text-[9px] tracking-[0.18em] uppercase text-white/40 mb-1.5">{label}</div>
      <div className="grid grid-cols-3 gap-1.5">{children}</div>
    </div>
  )
}

function Pill({ active, onClick, icon: Icon, label }) {
  return (
    <button
      onClick={onClick}
      className={[
        'flex flex-col items-center gap-1 rounded-lg border px-2 py-2 transition',
        'col-span-1',
        active
          ? 'border-[#2e7fff]/45 bg-[#2e7fff]/[0.12] text-[#a0d4ff]'
          : 'border-white/[0.06] bg-white/[0.02] text-white/65 hover:bg-white/[0.04] hover:text-white/90',
      ].join(' ')}
    >
      <Icon className="w-3.5 h-3.5" />
      <span className="font-mono text-[9.5px] text-center leading-tight">{label}</span>
    </button>
  )
}
